// Safe formula evaluator for formula and rollup fields (PHASED-PLAN F2, [spike]). A formula is read by a small recursive
// descent parser and evaluated against a scope of named values: no eval, no Function, no property access, no statements.
// Grammar: numbers, field names, + - * / with unary minus and parentheses, one comparison (> < >= <= = !=) between two
// values, SUM, AVG, MIN and MAX over any number of values or lists, and IF(test, then, else), which evaluates only the
// branch it takes. A blank field counts as 0. Every problem is a FormulaError that names it.

export class FormulaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FormulaError";
  }
}

/** What a field name stands for: a number, a list (a rollup over several records), or blank (counts as 0). */
export type FormulaValue = number | number[] | null | undefined;
export type FormulaScope = Record<string, FormulaValue>;

export const FORMULA_MAX_LENGTH = 500;
const MAX_TOKENS = 300;
const MAX_DEPTH = 24;
const MAX_VALUES = 10_000;
const AGGREGATES = ["SUM", "AVG", "MIN", "MAX"];
const COMPARISONS = [">", "<", ">=", "<=", "=", "!="];
const NUMBER = /^(\d+(\.\d+)?|\.\d+)/;
const NAME = /^[A-Za-z_][A-Za-z0-9_]*/;

type Tok =
  | { t: "num"; v: number; at: number }
  | { t: "id"; v: string; at: number }
  | { t: "op"; v: string; at: number }
  | { t: "(" | ")" | ","; at: number };

type Node =
  | { k: "num"; v: number }
  | { k: "ref"; name: string }
  | { k: "neg"; arg: Node }
  | { k: "bin"; op: string; l: Node; r: Node }
  | { k: "call"; fn: string; args: Node[] };

function tokenize(src: string): Tok[] {
  if (src.length > FORMULA_MAX_LENGTH) throw new FormulaError(`A formula is at most ${FORMULA_MAX_LENGTH} characters.`);
  const toks: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src.charAt(i);
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (toks.length >= MAX_TOKENS) throw new FormulaError("The formula has too many parts.");
    const rest = src.slice(i);
    const num = NUMBER.exec(rest);
    if (num) {
      toks.push({ t: "num", v: Number(num[0]), at: i });
      i += num[0].length;
      continue;
    }
    const name = NAME.exec(rest);
    if (name) {
      toks.push({ t: "id", v: name[0], at: i });
      i += name[0].length;
      continue;
    }
    const pair = rest.slice(0, 2);
    if (pair === ">=" || pair === "<=" || pair === "!=") {
      toks.push({ t: "op", v: pair, at: i });
      i += 2;
      continue;
    }
    if ("+-*/<>=".includes(ch)) {
      toks.push({ t: "op", v: ch, at: i });
      i++;
      continue;
    }
    if (ch === "(" || ch === ")" || ch === ",") {
      toks.push({ t: ch, at: i });
      i++;
      continue;
    }
    throw new FormulaError(`The character "${ch}" at position ${i + 1} is not allowed.`);
  }
  return toks;
}

class Parser {
  private at = 0;
  private depth = 0;
  private readonly toks: Tok[];

  constructor(toks: Tok[]) {
    this.toks = toks;
  }

  parse(): Node {
    if (!this.toks.length) throw new FormulaError("The formula is empty.");
    const node = this.comparison();
    if (this.at < this.toks.length) throw this.unexpected();
    return node;
  }

  private peek(): Tok | undefined {
    return this.toks[this.at];
  }

  private opIs(...ops: string[]): string | undefined {
    const t = this.peek();
    return t?.t === "op" && ops.includes(t.v) ? t.v : undefined;
  }

  private unexpected(): FormulaError {
    const t = this.peek();
    if (!t) return new FormulaError("The formula ends too early.");
    const shown = t.t === "num" ? String(t.v) : t.t === "id" || t.t === "op" ? t.v : t.t;
    return new FormulaError(`Unexpected "${shown}" at position ${t.at + 1}.`);
  }

  private enter(): void {
    if (++this.depth > MAX_DEPTH) throw new FormulaError("The formula is nested too deeply.");
  }

  private comparison(): Node {
    const l = this.additive();
    const op = this.opIs(...COMPARISONS);
    if (!op) return l;
    this.at++;
    const r = this.additive();
    if (this.opIs(...COMPARISONS)) throw new FormulaError("Compare two values at a time.");
    return { k: "bin", op, l, r };
  }

  private additive(): Node {
    let l = this.term();
    for (let op = this.opIs("+", "-"); op; op = this.opIs("+", "-")) {
      this.at++;
      l = { k: "bin", op, l, r: this.term() };
    }
    return l;
  }

  private term(): Node {
    let l = this.unary();
    for (let op = this.opIs("*", "/"); op; op = this.opIs("*", "/")) {
      this.at++;
      l = { k: "bin", op, l, r: this.unary() };
    }
    return l;
  }

  private unary(): Node {
    if (this.opIs("-")) {
      this.at++;
      this.enter();
      const arg = this.unary();
      this.depth--;
      return { k: "neg", arg };
    }
    return this.primary();
  }

  private primary(): Node {
    const t = this.peek();
    if (!t) throw new FormulaError("The formula ends too early.");
    if (t.t === "num") {
      this.at++;
      return { k: "num", v: t.v };
    }
    if (t.t === "(") {
      this.at++;
      this.enter();
      const inner = this.comparison();
      this.depth--;
      if (this.peek()?.t !== ")") throw new FormulaError(`The "(" at position ${t.at + 1} has no matching ")".`);
      this.at++;
      return inner;
    }
    if (t.t === "id") {
      this.at++;
      if (this.peek()?.t !== "(") return { k: "ref", name: t.v };
      return this.call(t.v, t.at);
    }
    throw this.unexpected();
  }

  private call(name: string, at: number): Node {
    const fn = name.toUpperCase();
    if (fn !== "IF" && !AGGREGATES.includes(fn)) throw new FormulaError(`There is no function ${name}. Use SUM, AVG, MIN, MAX or IF.`);
    this.at++;
    this.enter();
    const args: Node[] = [];
    if (this.peek()?.t === ")") {
      this.at++;
    } else {
      for (;;) {
        args.push(this.comparison());
        if (this.peek()?.t === ",") {
          this.at++;
          continue;
        }
        if (this.peek()?.t === ")") {
          this.at++;
          break;
        }
        throw this.unexpected();
      }
    }
    this.depth--;
    if (fn === "IF" && args.length !== 3) throw new FormulaError(`IF takes a test and two results (position ${at + 1}).`);
    if (fn !== "IF" && !args.length) throw new FormulaError(`${fn} needs at least one value.`);
    return { k: "call", fn, args };
  }
}

function parse(src: string): Node {
  return new Parser(tokenize(src)).parse();
}

function finite(x: number, name: string): number {
  if (!Number.isFinite(x)) throw new FormulaError(`The field ${name} is not a finite number.`);
  return x;
}

/** A field's value: a number, a list of numbers, or 0 for a blank field. */
function valueIn(name: string, scope: FormulaScope): number | number[] {
  if (!Object.hasOwn(scope, name)) throw new FormulaError(`There is no field ${name}.`);
  const v = scope[name];
  if (v === null || v === undefined) return 0;
  if (Array.isArray(v)) return v.map((x) => finite(x, name));
  return finite(v, name);
}

function binary(op: string, a: number, b: number): number {
  switch (op) {
    case "+":
      return a + b;
    case "-":
      return a - b;
    case "*":
      return a * b;
    case "/":
      if (b === 0) throw new FormulaError("You cannot divide by zero.");
      return a / b;
    case ">":
      return a > b ? 1 : 0;
    case "<":
      return a < b ? 1 : 0;
    case ">=":
      return a >= b ? 1 : 0;
    case "<=":
      return a <= b ? 1 : 0;
    case "=":
      return a === b ? 1 : 0;
    default:
      return a !== b ? 1 : 0;
  }
}

function scalar(n: Node, scope: FormulaScope): number {
  switch (n.k) {
    case "num":
      return n.v;
    case "ref": {
      const v = valueIn(n.name, scope);
      if (Array.isArray(v)) throw new FormulaError(`${n.name} is a list. Wrap it in SUM, AVG, MIN or MAX.`);
      return v;
    }
    case "neg":
      return -scalar(n.arg, scope);
    case "bin":
      return binary(n.op, scalar(n.l, scope), scalar(n.r, scope));
    case "call":
      return call(n.fn, n.args, scope);
  }
}

function call(fn: string, args: Node[], scope: FormulaScope): number {
  if (fn === "IF") return scalar(scalar(args[0]!, scope) !== 0 ? args[1]! : args[2]!, scope);
  const values: number[] = [];
  for (const a of args) {
    const part = a.k === "ref" ? valueIn(a.name, scope) : scalar(a, scope);
    const add = Array.isArray(part) ? part : [part];
    if (values.length + add.length > MAX_VALUES) throw new FormulaError("The list is too long to add up.");
    for (const x of add) values.push(x);
  }
  if (!values.length) return 0;
  if (fn === "SUM") return values.reduce((s, x) => s + x, 0);
  if (fn === "AVG") return values.reduce((s, x) => s + x, 0) / values.length;
  if (fn === "MIN") return Math.min(...values);
  return Math.max(...values);
}

/** The value of a formula over a scope. Throws FormulaError with the first problem found. */
export function evalFormula(src: string, scope: FormulaScope): number {
  const value = scalar(parse(src), scope);
  if (!Number.isFinite(value)) throw new FormulaError("The result is not a finite number.");
  return value;
}

/** The field names a formula reads, sorted and unique. Throws FormulaError when the formula does not parse. */
export function formulaNames(src: string): string[] {
  const names = new Set<string>();
  const walk = (n: Node): void => {
    if (n.k === "ref") names.add(n.name);
    else if (n.k === "neg") walk(n.arg);
    else if (n.k === "bin") {
      walk(n.l);
      walk(n.r);
    } else if (n.k === "call") for (const a of n.args) walk(a);
  };
  walk(parse(src));
  return [...names].sort();
}

/** Checks a formula's syntax and that every field it reads is in `fields`. Returns the first problem, or null. */
export function checkFormula(src: string, fields: readonly string[]): string | null {
  try {
    const unknown = formulaNames(src).find((n) => !fields.includes(n));
    return unknown ? `There is no field ${unknown}.` : null;
  } catch (err) {
    return err instanceof FormulaError ? err.message : "The formula is not valid.";
  }
}
